import React, { useState } from 'react';
import { Reorder, useDragControls } from 'framer-motion';
import { GripVerticalIcon, PlusIcon, XIcon } from 'lucide-react';
import { BUTTON_QUIET, FIELD, SECTION_HINT, SECTION_TITLE } from './adminStyles';
import type { ShootDayScheduleItem } from '../../utils/shootDaySource';

/**
 * The running order for the day.
 *
 * Always on the call sheet — it has no segment switch, deliberately. This is
 * the one section a live broadcast cares most about, and a switch that can
 * remove the schedule can produce a call sheet that isn't one.
 *
 * Lifted out of CallSheetBuilder.tsx on 13 Sep 2026.
 *
 * **Dragged by the grip — 27 Oct 2026, Tom's request.** A running order is
 * written in the order the day was planned, not the order it ends up in, and
 * retyping seven fields to move one segment up is how a call sheet goes out
 * wrong. Same pattern as the project tracker and the deliverables list:
 * `dragListener={false}` plus `touch-none` on the handle, so a thumb on a
 * phone moves the row and not the page.
 *
 * **The order is the array order, and nothing else.** `set_shoot_day_schedule`
 * writes `order_index` from the array position, so moving a row here is the
 * whole of it — there is no separate sort, and the times are never used to
 * re-sort behind your back. A segment can sit out of time order if that is
 * what the day does.
 *
 * **It saves with the form, not on drop.** Every other field in this builder
 * is kept until Save Call Sheet, and a list that persisted itself while the
 * fields beside it did not would be the odd one out.
 *
 * **Where suggests the day's locations — 28 Oct 2026.** It stays a free text
 * box rather than becoming a picker, because a real segment spans two places
 * often enough to matter: this sheet already has one reading "Movenpick
 * Resorts & Ula Restaurant". A `datalist` gives one tap for the common case
 * and costs nothing in the awkward one. The names it offers come from
 * Locations &amp; Access above, so the two agree without being wired together.
 */

/** The key a row is dragged by. Client-side only: `saveShootDaySchedule`
    maps named fields, so `uid` is never sent anywhere. */
export type ScheduleRow = ShootDayScheduleItem & {
  uid: string;
};
const LOCATION_LIST_ID = 'call-sheet-location-names';
interface CallSheetScheduleProps {
  items: ScheduleRow[];
  /** Names from Locations &amp; Access, offered under the Where field. */
  locationNames: string[];
  onAdd: () => void;
  onUpdate: (index: number, patch: Partial<ShootDayScheduleItem>) => void;
  onRemove: (index: number) => void;
  onReorder: (next: ScheduleRow[]) => void;
}
export function CallSheetSchedule({
  items,
  locationNames,
  onAdd,
  onUpdate,
  onRemove,
  onReorder
}: CallSheetScheduleProps) {
  return <section className="mt-14 border-t border-ruleDark pt-8" data-id="element-770" data-mp="2bajviwza25sb">
      <h2 className={SECTION_TITLE} data-id="element-771" data-mp="2ba1uzo5l15el">Schedule</h2>
      {items.length > 1 && <p className={SECTION_HINT} data-id="element-772" data-mp="2g8d1bowo4xs2">Drag a segment by its grip to move it up or down.</p>}

      {items.length === 0 && <p className="mt-6 font-display text-lg text-paper/40" data-id="element-773" data-mp="4odu9vk0410y">Nothing added yet.</p>}

      {/* One list for the whole section — every Where field points at it. */}
      <datalist id={LOCATION_LIST_ID} data-id="element-774" data-mp="3r0krvieyfbvy">
        {locationNames.map(name => <option key={name} value={name} data-id="element-775" data-mp="3m3km78m0ob5d" />)}
      </datalist>

      <Reorder.Group as="ul" axis="y" values={items} onReorder={onReorder} className="mt-6 list-none space-y-6 p-0" data-id="element-776" data-mp="3i17ewfrmg56h">
        {items.map((item, index) => <ScheduleSegment key={item.uid} item={item} movable={items.length > 1} hasLocations={locationNames.length > 0} onUpdate={patch => onUpdate(index, patch)} onRemove={() => onRemove(index)} data-id="element-777" data-mp="382ro6dwiaxxi" data-mpi={index} />)}
      </Reorder.Group>

      <button type="button" onClick={onAdd} className={`${BUTTON_QUIET} mt-6 flex items-center gap-2`} data-id="element-778" data-mp="11cbs98be8kch">
        <PlusIcon className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-779" data-mp="19czxrhr562br" />
        Add Schedule Item
      </button>
    </section>;
}
function ScheduleSegment({
  item,
  movable,
  hasLocations,
  onUpdate,
  onRemove
}: {
  item: ScheduleRow;
  movable: boolean;
  hasLocations: boolean;
  onUpdate: (patch: Partial<ShootDayScheduleItem>) => void;
  onRemove: () => void;
}) {
  const dragControls = useDragControls();
  const [carrying, setCarrying] = useState(false);
  const handleLabel = item.title.trim() || item.timeLabel.trim() || 'this segment';
  return <Reorder.Item value={item} dragListener={false} dragControls={dragControls} onDragStart={() => setCarrying(true)} onDragEnd={() => setCarrying(false)} animate={carrying ? {
    backgroundColor: 'rgba(255,255,255,0.05)',
    boxShadow: '0 18px 40px rgba(0,0,0,0.55)'
  } : {
    backgroundColor: 'rgba(255,255,255,0)',
    boxShadow: '0 0 0 rgba(0,0,0,0)'
  }} transition={{
    duration: 0.18,
    ease: [0.23, 1, 0.32, 1]
  }} style={{
    position: 'relative',
    zIndex: carrying ? 30 : 0
  }} className={['border p-5', carrying ? 'cursor-grabbing border-paper/40 ring-1 ring-paper/25' : 'border-paper/20'].join(' ')} data-id="element-780" data-mp="vfl3nzpy16dg">
      <div className="flex items-start gap-4" data-id="element-781" data-mp="1zix040ywab6">
        {movable ? <span role="button" tabIndex={0} onPointerDown={event => {
        event.preventDefault();
        dragControls.start(event);
      }} aria-label={`Reorder ${handleLabel}`} title="Drag to reorder" className="shrink-0 translate-y-2 touch-none cursor-grab text-paper/30 transition-colors duration-150 ease-out hover:text-paper/70 active:cursor-grabbing" data-id="element-782" data-mp="3t6n2uvldlqi0">
            <GripVerticalIcon className="h-5 w-5" strokeWidth={1.25} data-id="element-783" data-mp="uprhw3mw18tg" />
          </span> : <span className="w-5 shrink-0" aria-hidden="true" data-id="element-784" data-mp="ps9707pb8jit" />}

        <div className="grid flex-1 grid-cols-2 gap-4 sm:grid-cols-4" data-id="element-785" data-mp="3jjfs5vnqsmlf">
          <input value={item.timeLabel} onChange={event => onUpdate({
          timeLabel: event.target.value
        })} placeholder="Start (e.g. 07:00)" className={FIELD} data-id="element-786" data-mp="3jhcviss33v7q" />
          <input value={item.endTimeLabel} onChange={event => onUpdate({
          endTimeLabel: event.target.value
        })} placeholder="End (optional)" className={FIELD} data-id="element-787" data-mp="15gd996tcn49" />
          <input value={item.title} onChange={event => onUpdate({
          title: event.target.value
        })} placeholder="Item (e.g. Crew Call)" className={`${FIELD} col-span-2 sm:col-span-2`} data-id="element-788" data-mp="b34l90tfjr4d" />
          <input value={item.durationNote} onChange={event => onUpdate({
          durationNote: event.target.value
        })} placeholder="Duration (optional)" className={FIELD} data-id="element-789" data-mp="k3snyzddi51y" />
          <input value={item.peopleNote} onChange={event => onUpdate({
          peopleNote: event.target.value
        })} placeholder="Who (optional)" className={FIELD} data-id="element-790" data-mp="2lxs0tp658ugr" />
          <input value={item.locationNote} onChange={event => onUpdate({
          locationNote: event.target.value
        })} list={LOCATION_LIST_ID} placeholder={hasLocations ? 'Where — pick or type' : 'Where (optional)'} className={FIELD} data-id="element-791" data-mp="24mcz2l3h4cbz" />
          <input value={item.note} onChange={event => onUpdate({
          note: event.target.value
        })} placeholder="Note (optional)" className={`${FIELD} col-span-2 sm:col-span-1`} data-id="element-792" data-mp="2sbxsjxx96rzi" />
        </div>

        <button type="button" onClick={onRemove} aria-label="Remove schedule item" className="shrink-0 text-paper/30 transition-colors duration-150 ease-out hover:text-paper" data-id="element-793" data-mp="2jahhdnym6lu8">
          <XIcon className="h-4 w-4" strokeWidth={1.5} data-id="element-794" data-mp="17y6yqadl463i" />
        </button>
      </div>
    </Reorder.Item>;
}